import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import { validateGeminiKey } from "../../../utils/geminiUtils";


const ColdEmailGenerate = () => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [enhancingPrompt, setEnhancingPrompt] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form state
  const [recipientName, setRecipientName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [keyDetails, setKeyDetails] = useState("");
  const [tone, setTone] = useState("professional");
  const [customPrompt, setCustomPrompt] = useState("");
  const [enhancedPrompt, setEnhancedPrompt] = useState("");
  const [isCustomPromptEdited, setIsCustomPromptEdited] = useState(false);

  // User data
  

  // Generated email
  const [generatedEmail, setGeneratedEmail] = useState(null);

  // API keys state
  const credits = "Unlimited";

  // Tone options
  const toneOptions = [
    { value: "friendly", label: "Friendly" },
    { value: "formal", label: "Formal" },
    { value: "persuasive", label: "Persuasive" },
    { value: "promotional", label: "Promotional" },
    { value: "casual", label: "Casual" },
    { value: "professional", label: "Professional" },
    { value: "apologetic", label: "Apologetic" },
    { value: "appreciative", label: "Appreciative" },
  ];

  // Fetch API keys from endpoint

  // Get random API key  // Fetch user data and API keys when user is authenticated
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        return;
      }
      try {
const userResponse = await axios.get(`https://server.enjoywiki.com/youtubeai-may/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Initialize custom prompt on component mount
  useEffect(() => {
    setCustomPrompt(
      "Generate a personalized email template based on the provided information."
    );
  }, []);

  // Construct the AI prompt
  const constructPrompt = () => {
    const basePrompt = `You are an AI-powered Email Template Generator. Your task is to create personalized, professional email templates based on the user's inputs.
    
    The user has provided:
    - Recipient Name: ${recipientName}
    - Sender Name: ${senderName}
    - Purpose of the Email: ${purpose}
    - Key Details or Hints: ${keyDetails}
    - Desired Tone: ${tone}
    
    Your output must:
    1. Include a subject line.
    2. Start with a personalized greeting using the recipient's name.
    3. Write a clear, engaging, and professional email body (120–200 words).
    4. Incorporate the purpose and hints naturally (avoid generic/robotic tone).
    5. End with a clear closing and the sender's name.
    6. Ensure the email is ready-to-send without requiring edits.
    
    Format the response like this:
    Subject: [Generated Subject Line]  
    Body:  
    [Generated Email Body]`;

    // Add the custom prompt or enhanced prompt
    return `${basePrompt} ${enhancedPrompt || customPrompt}`;
  };

  // Enhance the prompt using gemini-2.5-flash-lite model
  const enhancePrompt = async () => {
    if (!customPrompt.trim()) {
      setError("Please enter a custom prompt to enhance");
      return;
    }
    setEnhancingPrompt(true);
    setError("");
    setSuccess("");
    try {
      const MODEL_ID = localStorage.getItem("geminiTextModel") || "gemini-2.5-flash-lite";
      const GENERATE_CONTENT_API = "generateContent";
            const apiKey = await validateGeminiKey(user.email);
      if (!apiKey) {
        setGenerating(false);
        return;
      }
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:${GENERATE_CONTENT_API}?key=${apiKey}`;

      // Use the current custom prompt in the enhancement request
      const enhancePromptText = `Enhance this email generation prompt for better results: "${customPrompt}". 
      Make it more descriptive and effective for generating high-quality, personalized email templates. 
      The enhanced prompt should focus on creating professional, engaging emails that match the specified tone and purpose.
      Consider these details in your enhancement:
      - Recipient: ${recipientName || "[Not specified]"}
      - Sender: ${senderName || "[Not specified]"}
      - Purpose: ${purpose || "[Not specified]"}
      - Key Details: ${keyDetails || "[Not specified]"}
      - Tone: ${tone}`;

      const requestBody = {
        contents: [
          {
            role: "user",
            parts: [
              {
                text: enhancePromptText,
              },
            ],
          },
        ],
        generationConfig: {
          thinkingConfig: {
            thinkingBudget: 0,
          },
        },
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const enhancedText = data.candidates[0].content.parts[0].text;
        setEnhancedPrompt(enhancedText);
        setSuccess("Prompt enhanced successfully!");
      } else {
        throw new Error("No enhancement received from API");
      }
    } catch (err) {
      console.error("Error enhancing prompt:", err);
      setError(`Error enhancing prompt: ${err.message}`);
    } finally {
      setEnhancingPrompt(false);
    }
  };

  // Generate an email template
  const generateEmail = async () => {
    if (!recipientName.trim() || !senderName.trim() || !purpose.trim()) {
      setError("Recipient name, sender name, and purpose are required");
      return;
    }
    setGenerating(true);
    setError("");
    setSuccess("");

    try {
      // Deduct 1 credit
      

      

      // Update credits locally
      

      // Generate email
      const MODEL_ID = localStorage.getItem("geminiTextModel") || "gemini-2.5-flash-lite";
            const apiKey = await validateGeminiKey(user.email);
      if (!apiKey) {
        setGenerating(false);
        return;
      }
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;

      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      let emailData = null;

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        const generatedText = data.candidates[0].content.parts[0].text;

        // Parse the response to extract subject and body
        let subject = "";
        let body = "";

        // Check if the response contains the expected format
        if (
          generatedText.includes("Subject:") &&
          generatedText.includes("Body:")
        ) {
          // Split the text at the first occurrence of "Body:"
          const parts = generatedText.split("Body:");
          if (parts.length >= 2) {
            // Extract subject from the first part
            const subjectPart = parts[0];
            const subjectMatch = subjectPart.match(/Subject:\s*(.+?)(?=\n|$)/);
            if (subjectMatch) {
              subject = subjectMatch[1].trim();
            }

            // The rest is the body (join all parts after the first in case "Body:" appears in the content)
            body = parts.slice(1).join("Body:").trim();
          }
        } else {
          // Fallback if the format isn't as expected
          // Try to extract subject from the first line
          const lines = generatedText.split("\n");
          if (lines.length > 0 && lines[0].toLowerCase().includes("subject:")) {
            subject = lines[0].replace(/^Subject:\s*/i, "").trim();
            body = lines.slice(1).join("\n").trim();
          } else {
            // If no subject line, use a default
            subject = `Email from ${senderName}`;
            body = generatedText;
          }
        }

        // Clean up body - remove any remaining "Subject:" or "Body:" tags
        body = body
          .replace(/^Subject:\s*.+$/m, "")
          .replace(/^Body:\s*/m, "")
          .trim();

        emailData = {
          subject: subject,
          body: body,
          prompt: prompt,
          timestamp: new Date().toISOString(),
        };
      }

      if (!emailData) {
        console.log("Full API response:", JSON.stringify(data, null, 2));
        throw new Error("No email was generated from the API response");
      }

      setGeneratedEmail(emailData);
      setSuccess("Email template generated successfully!");
    } catch (err) {
      console.error("Error generating email:", err);
      setError(`Error: ${err.message}`);

      
    } finally {
      setGenerating(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSuccess(`${type} copied to clipboard!`);
      })
      .catch((err) => {
        setError(`Failed to copy ${type.toLowerCase()} to clipboard`);
        console.error("Error copying text: ", err);
      });
  };

  // Copy the subject to clipboard
  const copySubjectToClipboard = () => {
    if (!generatedEmail) return;
    copyToClipboard(generatedEmail.subject, "Subject");
  };

  // Copy the body to clipboard
  const copyBodyToClipboard = () => {
    if (!generatedEmail) return;
    copyToClipboard(generatedEmail.body, "Body");
  };

  // Copy the entire email to clipboard
  const copyEmailToClipboard = () => {
    if (!generatedEmail) return;
    const emailText = `Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
    copyToClipboard(emailText, "Email");
  };

  // Download the generated email as a text file
  const downloadEmail = () => {
    if (!generatedEmail) return;

    const emailText = `Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
    const blob = new Blob([emailText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `email-template-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // Buy more credits
  

  // Reset form
  const resetForm = () => {
    setRecipientName("");
    setSenderName("");
    setPurpose("");
    setKeyDetails("");
    setTone("professional");
    setCustomPrompt(
      "Generate a personalized email template based on the provided information."
    );
    setEnhancedPrompt("");
    setIsCustomPromptEdited(false);
    setGeneratedEmail(null);
    setError("");
    setSuccess("");
  };

  if (loadingAuth || loading ) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Please log in to use the Email Template Generator</h2>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="email-template-generator">
      <div className="header">
        <h1 className="app-title">AI Email Template Generator</h1>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="main-content">
        {/* Left Panel - Inputs */}
        <div className="form-section">
          {/* Two-column row for Recipient and Sender Names */}
          <div className="form-row">
            <div className="form-group half-width">
              <label className="form-label" htmlFor="recipientName">
                Recipient Name *
              </label>
              <div className="input-with-icon">
                <input
                  id="recipientName"
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Enter recipient's name"
                  required
                  className="text-input"
                />
                <div className="input-icon user-icon"></div>
              </div>
            </div>

            <div className="form-group half-width">
              <label className="form-label" htmlFor="senderName">
                Sender Name *
              </label>
              <div className="input-with-icon">
                <input
                  id="senderName"
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  className="text-input"
                />
                <div className="input-icon sender-icon"></div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="purpose">
              Purpose of Email *
            </label>
            <div className="input-with-icon">
              <input
                id="purpose"
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="e.g. Meeting request, Thank you note, Follow-up"
                required
                className="text-input"
              />
              <div className="input-icon purpose-icon"></div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="keyDetails">
              Key Details or Hints
            </label>
            <textarea
              id="keyDetails"
              value={keyDetails}
              onChange={(e) => setKeyDetails(e.target.value)}
              placeholder="Add specific details to include in the email"
              rows={3}
              className="textarea-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="tone">
              Desired Tone
            </label>
            <div className="select-wrapper">
              <select
                id="tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="select-input"
              >
                {toneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="select-arrow"></div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="customPrompt">
              Custom Prompt
            </label>
            <textarea
              id="customPrompt"
              value={customPrompt}
              onChange={(e) => {
                setCustomPrompt(e.target.value);
                setIsCustomPromptEdited(true);
              }}
              placeholder="Add any specific instructions for the AI..."
              rows={3}
              className="textarea-input"
            />
            <button
              type="button"
              className={`enhance-btn ${enhancingPrompt ? "loading" : ""}`}
              onClick={enhancePrompt}
              disabled={
                enhancingPrompt || !customPrompt.trim() 
              }
            >
              {enhancingPrompt ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>Enhancing...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon magic-icon"></span>
                  <span>Enhance Prompt</span>
                </>
              )}
            </button>
          </div>

          {enhancedPrompt && (
            <div className="form-group">
              <label className="form-label" htmlFor="enhancedPrompt">
                Enhanced Prompt
              </label>
              <textarea
                id="enhancedPrompt"
                value={enhancedPrompt}
                onChange={(e) => setEnhancedPrompt(e.target.value)}
                rows={3}
                className="textarea-input enhanced"
              />
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className={`generate-btn ${generating ? "loading" : ""}`}
              onClick={generateEmail}
              disabled={
                generating ||
                !recipientName.trim() ||
                !senderName.trim() ||
                !purpose.trim() 
              }
            >
              {generating ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span className="btn-icon generate-icon"></span>
                  <span>Generate Email</span>
                </>
              )}
            </button>
            <button type="button" className="reset-btn" onClick={resetForm}>
              <span className="btn-icon reset-icon"></span>
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="preview-section">
          <div className="preview-header">
            <h2 className="preview-title">Generated Email</h2>
          </div>

          <div className="preview-container">
            {generating ? (
              <div className="preview-loading">
                <div className="ai-generating-animation">
                  <div className="ai-icon">
                    <div className="ai-icon-inner"></div>
                  </div>
                  <div className="ai-text">
                    <p>AI is generating your email template...</p>
                    <div className="ai-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            ) : generatedEmail ? (
              <div className="generated-email">
                <div className="email-preview">
                  <div className="email-section">
                    <div className="section-header">
                      <h3 className="section-title">Subject</h3>
                      <button
                        className="copy-section-btn"
                        onClick={copySubjectToClipboard}
                        title="Copy Subject"
                      >
                        <span className="copy-icon"></span>
                      </button>
                    </div>
                    <div className="email-subject">
                      {generatedEmail.subject}
                    </div>
                  </div>

                  <div className="email-section">
                    <div className="section-header">
                      <h3 className="section-title">Body</h3>
                      <button
                        className="copy-section-btn"
                        onClick={copyBodyToClipboard}
                        title="Copy Body"
                      >
                        <span className="copy-icon"></span>
                      </button>
                    </div>
                    <div className="email-body">
                      {generatedEmail.body
                        .split("\n")
                        .map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="email-actions">
                  <button
                    className="action-btn copy-all-btn"
                    onClick={copyEmailToClipboard}
                  >
                    <span className="btn-icon copy-icon"></span>
                    <span>Copy Entire Email</span>
                  </button>
                  <button
                    className="action-btn download-btn"
                    onClick={downloadEmail}
                  >
                    <span className="btn-icon download-icon"></span>
                    <span>Download as Text</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="preview-placeholder">
                <p>Your generated email template will appear here</p>
                <div className="email-placeholder">
                  <div className="placeholder-section">
                    <div className="section-header">
                      <h3 className="section-title">Subject</h3>
                    </div>
                    <div className="placeholder-subject">
                      [Generated Subject]
                    </div>
                  </div>

                  <div className="placeholder-section">
                    <div className="section-header">
                      <h3 className="section-title">Body</h3>
                    </div>
                    <div className="placeholder-body">
                      <p>Dear [Recipient Name],</p>
                      <p>Your personalized email content will appear here...</p>
                      <p>Best regards,</p>
                      <p>[Your Name]</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .email-template-generator {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
          color: #333;
        }

        /* Header Styles */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 25px 30px;
          background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        }

        .app-title {
          font-size: 32px;
          font-weight: 700;
          color: white;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .credits-display {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .credits-info {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.15);
          padding: 10px 18px;
          border-radius: 30px;
          backdrop-filter: blur(5px);
        }

        .credits-label {
          color: rgba(255, 255, 255, 0.85);
          font-size: 15px;
          margin-right: 8px;
          font-weight: 500;
        }

        .credits-value {
          color: white;
          font-weight: 700;
          font-size: 18px;
        }

        .buy-credits-btn {
          background: white;
          color: #2c3e50;
          border: none;
          padding: 10px 18px;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 600;
          font-size: 15px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .buy-credits-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Error and Success Messages */
        .error-message {
          color: #d32f2f;
          background-color: #ffebee;
          padding: 14px 18px;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #d32f2f;
          font-weight: 500;
        }

        .success-message {
          color: #388e3c;
          background-color: #e8f5e9;
          padding: 14px 18px;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #388e3c;
          font-weight: 500;
        }

        /* Main Content */
        .main-content {
          display: flex;
          gap: 30px;
          margin-bottom: 40px;
        }

        /* Form Section */
        .form-section {
          flex: 1;
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        /* Form Row for Two Columns */
        .form-row {
          display: flex;
          gap: 20px;
          margin-bottom: 25px;
        }

        .half-width {
          flex: 1;
        }

        .form-group {
          margin-bottom: 25px;
        }

        .form-label {
          display: block;
          margin-bottom: 10px;
          font-weight: 600;
          font-size: 15px;
          color: #444;
        }

        /* Input Styles */
        .input-with-icon {
          position: relative;
        }

        .text-input,
        .textarea-input,
        .select-input {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          box-sizing: border-box;
          font-size: 15px;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .text-input:focus,
        .textarea-input:focus,
        .select-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .textarea-input {
          resize: vertical;
          min-height: 100px;
        }

        .textarea-input.enhanced {
          background: #f9f9f9;
          border-color: #3498db;
        }

        .input-icon,
        .select-arrow {
          position: absolute;
          right: 18px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .user-icon,
        .sender-icon,
        .purpose-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
        }

        .user-icon {
          background: #3498db;
        }

        .sender-icon {
          background: #2980b9;
        }

        .purpose-icon {
          background: #1abc9c;
        }

        .select-wrapper {
          position: relative;
        }

        .select-arrow {
          width: 10px;
          height: 10px;
          border-left: 2px solid #3498db;
          border-bottom: 2px solid #3498db;
          transform: translateY(-50%) rotate(-45deg);
        }

        /* Buttons */
        .enhance-btn,
        .generate-btn,
        .reset-btn,
        .action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 22px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 15px;
          transition: all 0.3s ease;
        }

        .enhance-btn {
          background: #f39c12;
          color: white;
          margin-top: 12px;
          width: 100%;
        }

        .enhance-btn:hover:not(:disabled) {
          background: #e67e22;
        }

        .enhance-btn:disabled {
          background: #bdc3c7;
          color: #7f8c8d;
          cursor: not-allowed;
        }

        .form-actions {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }

        .generate-btn {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
          flex: 1;
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
        }

        .generate-btn:disabled {
          background: #bdc3c7;
          color: #7f8c8d;
          cursor: not-allowed;
        }

        .reset-btn {
          background: #ecf0f1;
          color: #7f8c8d;
        }

        .reset-btn:hover {
          background: #bdc3c7;
        }

        .btn-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s ease-in-out infinite;
        }

        .enhance-btn .btn-spinner {
          border-top-color: white;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .btn-icon {
          width: 18px;
          height: 18px;
          border-radius: 50%;
        }

        .magic-icon {
          background: linear-gradient(
            45deg,
            #f09433 0%,
            #e6683c 25%,
            #dc2743 50%,
            #cc2366 75%,
            #bc1888 100%
          );
        }

        .generate-icon {
          background: white;
        }

        .reset-icon {
          background: #7f8c8d;
        }

        .copy-icon {
          width: 18px;
          height: 18px;
          background: white;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect width='14' height='14' x='8' y='8' rx='2' ry='2'%3E%3C/rect%3E%3Cpath d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2'%3E%3C/path%3E%3C/svg%3E")
            no-repeat center;
          mask-size: contain;
        }

        .download-icon {
          background: white;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'%3E%3C/path%3E%3Cpolyline points='7 10 12 15 17 10'%3E%3C/polyline%3E%3Cline x1='12' y1='15' x2='12' y2='3'%3E%3C/line%3E%3C/svg%3E")
            no-repeat center;
          mask-size: contain;
        }

        /* Preview Section */
        .preview-section {
          flex: 1;
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .preview-header {
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e0e0e0;
        }

        .preview-title {
          font-size: 22px;
          font-weight: 600;
          margin: 0;
          color: #2c3e50;
        }

        .preview-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .generated-email,
        .preview-placeholder {
          text-align: center;
          width: 100%;
        }

        .email-preview {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 25px;
          margin-bottom: 25px;
          text-align: left;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .email-section {
          margin-bottom: 25px;
        }

        .email-section:last-child {
          margin-bottom: 0;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #3498db;
          margin: 0;
        }

        .copy-section-btn {
          background: #3498db;
          color: white;
          border: none;
          border-radius: 6px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .copy-section-btn:hover {
          background: #2980b9;
        }

        .email-subject {
          font-size: 18px;
          font-weight: 500;
          line-height: 1.4;
          color: #2c3e50;
          padding: 12px 15px;
          background: white;
          border-radius: 6px;
          border-left: 4px solid #3498db;
        }

        .email-body {
          line-height: 1.6;
          color: #34495e;
          padding: 15px;
          background: white;
          border-radius: 6px;
          border-left: 4px solid #1abc9c;
        }

        .email-body p {
          margin-bottom: 12px;
        }

        .email-body p:last-child {
          margin-bottom: 0;
        }

        .email-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
        }

        .action-btn {
          padding: 12px 18px;
          border-radius: 8px;
          font-size: 14px;
        }

        .copy-all-btn {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
        }

        .download-btn {
          background: linear-gradient(135deg, #1abc9c 0%, #16a085 100%);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .email-placeholder {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 25px;
          text-align: left;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .placeholder-section {
          margin-bottom: 25px;
        }

        .placeholder-section:last-child {
          margin-bottom: 0;
        }

        .placeholder-subject {
          font-size: 18px;
          font-weight: 500;
          color: #7f8c8d;
          padding: 12px 15px;
          background: white;
          border-radius: 6px;
          border-left: 4px solid #bdc3c7;
        }

        .placeholder-body {
          color: #95a5a6;
          line-height: 1.6;
          padding: 15px;
          background: white;
          border-radius: 6px;
          border-left: 4px solid #bdc3c7;
        }

        .placeholder-body p {
          margin-bottom: 12px;
        }

        .placeholder-body p:last-child {
          margin-bottom: 0;
        }

        /* Loading States */
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        }

        .loading-spinner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 5px solid rgba(52, 152, 219, 0.2);
          border-radius: 50%;
          border-top-color: #3498db;
          animation: spin 1s ease-in-out infinite;
        }

        .preview-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          width: 100%;
        }

        .ai-generating-animation {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 25px;
        }

        .ai-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
        }

        .ai-icon-inner {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.7);
          }
          70% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(52, 152, 219, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(52, 152, 219, 0);
          }
        }

        .ai-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .ai-text p {
          font-size: 18px;
          font-weight: 500;
          color: #34495e;
        }

        .ai-dots {
          display: flex;
          gap: 6px;
        }

        .ai-dots span {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #3498db;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .ai-dots span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .ai-dots span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        /* Auth Container */
        .auth-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
        }

        .auth-card {
          background: white;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          text-align: center;
          max-width: 400px;
          width: 100%;
        }

        .auth-card h2 {
          margin-bottom: 25px;
          color: #2c3e50;
        }

        .login-btn {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
          border: none;
          padding: 12px 25px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .login-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .main-content {
            flex-direction: column;
          }

          .email-template-generator {
            padding: 15px;
          }

          .header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }

          .form-row {
            flex-direction: column;
            gap: 0;
          }

          .preview-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }

          .ai-generating-animation {
            padding: 20px;
          }

          .email-actions {
            flex-direction: column;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .copy-section-btn {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default ColdEmailGenerate;
